import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, MessageCircle, Search } from 'lucide-react';
import AppShell from '../components/AppShell';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { RecordDetail } from '../components/mylife/RecordDetail';
import { CommentThread } from '../components/notes/CommentThread';
import { RecordCard, eyebrowDate } from '../components/mylife/RecordCard';
import { DECRYPT_BATCH } from '../hooks/useLazyRecords';
import { collectionsApi, notesApi, recordsApi } from '../lib/api';
import { colourClass } from '../lib/highlights';
import { getActingOnBehalfOf } from '../lib/sharedAccess';

/**
 * Collections — the person's own named sets of records and highlighted
 * passages ("About my brother", "Things to ask the council"). Saved from a
 * record, a highlight, or while browsing My Life. Names, notes and the
 * highlighted words are sealed with their key (routers/notes.py).
 *
 * Laid out like the organisation page's Cabinet: collections down the side
 * as drawers (with "Everything saved" first), the contents in the middle as
 * the same record cards, and a toolbar to search, narrow and sort. Search
 * runs here in the browser over what's already been opened — nothing about
 * it is sent to or kept by the server.
 */

const SORTS = {
  newest: { label: 'Newest saved', key: (e) => e.savedAt, desc: true },
  oldest: { label: 'Oldest saved', key: (e) => e.savedAt, desc: false },
  date: { label: 'In date order', key: (e) => e.record?.date || e.record?.approximate_date || '', desc: false },
};

const KINDS = [
  { id: 'all', label: 'Everything' },
  { id: 'record', label: 'Records' },
  { id: 'highlight', label: 'Passages' },
];

export const Collections = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [collections, setCollections] = useState(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(false);
  const [records, setRecords] = useState({});
  const [highlights, setHighlights] = useState({});
  // Comments on what's saved (the person's own and anyone they share with),
  // keyed "record:<id>" / "highlight:<id>".
  const [comments, setComments] = useState({});
  const [composeFor, setComposeFor] = useState(null); // which item's new-comment box is open
  // Until everything saved has been opened an item says "Opening…", never
  // "isn't there any more".
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [sort, setSort] = useState('newest');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [openRecord, setOpenRecord] = useState(null);
  const selectedId = params.get('c');

  const load = useCallback(async () => {
    try {
      const r = await collectionsApi.list();
      setLocked(!!r.data?.locked);
      setCollections(r.data?.collections || []);
    } catch {
      setError(true);
      setCollections([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => (collections || []).find((c) => c.id === selectedId) || null, [collections, selectedId]);

  const loadComments = useCallback(async (cols) => {
    const items = (cols || []).flatMap((c) => c.items);
    const byType = { record: new Set(), highlight: new Set() };
    items.forEach((i) => byType[i.target_type]?.add(i.target_id));
    const next = {};
    for (const [type, ids] of Object.entries(byType)) {
      if (!ids.size) continue;
      const notes = (await notesApi.list({ kind: 'comment', target_type: type, target_ids: [...ids] })).data?.notes || [];
      notes.filter((n) => n.kind === 'comment').forEach((n) => {
        const key = `${n.target_type}:${n.target_id}`;
        (next[key] = next[key] || []).push(n);
      });
    }
    setComments(next);
  }, []);

  // Open everything saved, across every collection, so "Everything saved"
  // and search work: highlights (and the records they're on) via notes,
  // records via the usual decrypting list, in batches.
  const itemKey = useMemo(
    () => (collections || []).flatMap((c) => c.items.map((i) => `${i.target_type}:${i.target_id}`)).sort().join('|'),
    [collections],
  );
  useEffect(() => {
    if (!collections) return undefined;
    let live = true;
    const items = collections.flatMap((c) => c.items);
    const hlIds = [...new Set(items.filter((i) => i.target_type === 'highlight').map((i) => i.target_id))];
    setOpened(false);
    (async () => {
      let hl = highlights;
      if (hlIds.some((id) => !hl[id])) {
        const all = (await notesApi.list({ kind: 'highlight' })).data?.notes || [];
        hl = { ...hl, ...Object.fromEntries(all.filter((n) => hlIds.includes(n.id)).map((n) => [n.id, n])) };
        if (live) setHighlights(hl);
      }
      const recIds = [...new Set([
        ...items.filter((i) => i.target_type === 'record').map((i) => i.target_id),
        ...hlIds.map((id) => hl[id]?.target_id).filter(Boolean),
      ])].filter((id) => !records[id]);
      await loadComments(collections).catch(() => {});
      for (let i = 0; i < recIds.length; i += DECRYPT_BATCH) {
        const entries = (await recordsApi.list(null, recIds.slice(i, i + DECRYPT_BATCH))).data?.records || [];
        if (!live) return;
        setRecords((r) => ({ ...r, ...Object.fromEntries(entries.map((e) => [e.id, e])) }));
      }
    })().catch(() => {}).finally(() => { if (live) setOpened(true); });
    return () => { live = false; };
  }, [itemKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // One entry per thing saved. In "Everything saved" a record kept in two
  // collections shows once, naming both.
  const entries = useMemo(() => {
    const source = selected ? [selected] : (collections || []);
    const byTarget = new Map();
    source.forEach((c) => c.items.forEach((item) => {
      const key = `${item.target_type}:${item.target_id}`;
      const hl = item.target_type === 'highlight' ? highlights[item.target_id] : null;
      const recId = hl ? hl.target_id : (item.target_type === 'record' ? item.target_id : null);
      const existing = byTarget.get(key);
      if (existing) {
        existing.collections.push(c);
        if (item.note) existing.notes.push(item.note);
        if ((item.created_at || '') > existing.savedAt) existing.savedAt = item.created_at || '';
        return;
      }
      byTarget.set(key, {
        key, item, kind: item.target_type, highlight: hl, record: recId ? records[recId] : null, recId,
        collections: [c], notes: item.note ? [item.note] : [], savedAt: item.created_at || '',
      });
    }));
    return [...byTarget.values()];
  }, [collections, selected, highlights, records]);

  const shown = useMemo(() => {
    let list = entries;
    if (kind !== 'all') list = list.filter((e) => e.kind === kind);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((e) => [
        e.record?.assembled_label, e.record?.doc_type, ...(e.record?.organisation_names || []),
        e.highlight?.quote, ...e.notes, ...e.collections.map((c) => c.name),
        ...(comments[e.key] || []).map((n) => n.text),
      ].some((t) => t && String(t).toLowerCase().includes(q)));
    }
    const { key, desc } = SORTS[sort];
    return [...list].sort((a, b) => (desc ? String(key(b)).localeCompare(String(key(a))) : String(key(a)).localeCompare(String(key(b)))));
  }, [entries, kind, search, sort, comments]);

  const totalSaved = useMemo(() => new Set((collections || []).flatMap((c) => c.items.map((i) => `${i.target_type}:${i.target_id}`))).size, [collections]);

  const create = async () => {
    if (!newName.trim()) return;
    const c = (await collectionsApi.create(newName.trim())).data;
    setNewName('');
    setCreating(false);
    setCollections((cs) => [...(cs || []), c]);
    setParams({ c: c.id });
  };

  const rename = async () => {
    if (!renaming?.name?.trim()) return;
    await collectionsApi.rename(renaming.id, renaming.name.trim());
    setCollections((cs) => cs.map((c) => (c.id === renaming.id ? { ...c, name: renaming.name.trim() } : c)));
    setRenaming(null);
  };

  const remove = async () => {
    await collectionsApi.remove(confirmDelete.id);
    setCollections((cs) => cs.filter((c) => c.id !== confirmDelete.id));
    setConfirmDelete(null);
    setParams({});
  };

  const removeItem = async (item) => {
    await collectionsApi.removeItem(selected.id, item.id);
    setCollections((cs) => cs.map((c) => (c.id === selected.id ? { ...c, items: c.items.filter((i) => i.id !== item.id) } : c)));
  };

  const choose = (id) => { setParams(id ? { c: id } : {}); setRenaming(null); };

  const newCollectionForm = (autoFocus = false) => (
    <div className="flex gap-2">
      <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); if (e.key === 'Escape') setCreating(false); }}
        placeholder="Name it, e.g. About my brother" aria-label="Name a new collection" autoFocus={autoFocus} data-testid="collections-new" />
      <Button size="sm" onClick={create} disabled={!newName.trim()} data-testid="collections-create">Add</Button>
    </div>
  );

  const RecordLine = ({ rec }) => (
    <>
      <span className="pd-mono text-ink-55 block">{eyebrowDate(rec.date || rec.approximate_date, rec.date_human)} · {rec.doc_type || 'Record'}</span>
      <span className="text-[16px] font-semibold text-ink block mt-0.5">{rec.assembled_label || rec.doc_type || 'A record'}</span>
    </>
  );

  const Pill = ({ on, onClick, children, testId }) => (
    <button type="button" onClick={onClick} aria-pressed={on} data-testid={testId}
      className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors duration-state ${on ? 'bg-brand-wash text-brand-ink border-brand-wash' : 'bg-ground text-ink-70 border-rule hover:bg-inset'}`}>
      {children}
    </button>
  );

  // The conversation under a saved record or passage — the person's own
  // comments and anyone's they share with, with replies — nested in from
  // the left so it reads as belonging to the thing above it.
  // The conversation about a saved record or passage — the person's own
  // comments and anyone's they share with, with replies — sits inside that
  // item's bubble, indented, so it's never unclear what it's about.
  const renderComments = (entry) => {
    const count = (comments[entry.key] || []).length;
    if (!count && composeFor !== entry.key) return null;
    return (
      <div className="ml-6 sm:ml-12 max-w-[68ch]">
        <CommentThread
          targetType={entry.kind} targetId={entry.item.target_id} tone="surface" compact
          comments={comments[entry.key] || []} onChanged={() => loadComments(collections)}
          composerOpen={composeFor === entry.key}
          onComposerChange={(open) => setComposeFor(open ? entry.key : null)}
          testId="collection-comments"
        />
      </div>
    );
  };

  // Under what was saved, the way a social feed does it: the comment button
  // (with how many there are) on the left, and on the right the collections
  // it's in as pills — or, inside one collection, the way to take it out.
  const renderBar = (entry, canComment = true) => {
    const count = (comments[entry.key] || []).length;
    const readOnly = (() => { const a = getActingOnBehalfOf(); return !!a && a.permission !== 'read_write'; })();
    return (
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {!canComment || (readOnly && !count) ? <span /> : (
          <button
            type="button"
            onClick={() => !readOnly && setComposeFor(composeFor === entry.key ? null : entry.key)}
            aria-label={count ? `${count} comment${count === 1 ? '' : 's'}${readOnly ? '' : ' — write another'}` : 'Comment'}
            aria-expanded={composeFor === entry.key}
            className="-ml-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-ink-70 hover:text-ink hover:bg-inset transition-colors duration-state"
            data-testid="collection-item-comment"
          >
            <MessageCircle className="w-[18px] h-[18px]" strokeWidth={1.75} aria-hidden="true" />
            <span>{count || 'Comment'}</span>
          </button>
        )}
        {selected ? (
          <button type="button" className="text-sm text-ink-55 hover:text-ink" onClick={() => removeItem(entry.item)} data-testid="collection-item-remove">
            Remove from this collection
          </button>
        ) : (
          <div className="flex flex-wrap gap-1.5" aria-label="In your collections" data-testid="collection-item-collections">
            {entry.collections.map((c) => (
              <button key={c.id} type="button" onClick={() => choose(c.id)}
                className="inline-flex items-center rounded-full px-[11px] py-1 text-[13px] font-medium leading-tight bg-surface text-ink-70 hover:text-ink hover:bg-inset transition-colors duration-state">
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderNotes = (entry) => (entry.notes.length > 0
    ? <div className="space-y-1">{entry.notes.map((n, i) => <p key={i} className="text-[15px] text-ink-70">{n}</p>)}</div>
    : null);

  // One bubble per thing saved: what it is, what was saved, where it's
  // kept, then the conversation about it — all inside the same box.
  const renderEntry = (entry) => {
    const { item, record, highlight } = entry;
    const missing = item.target_type === 'record' ? !record : !highlight;
    if (missing && !opened) {
      return <li key={entry.key} className="rounded-lg bg-ground p-5 text-[15px] text-ink-55" data-testid="collection-item">Opening…</li>;
    }
    if (item.target_type === 'record') {
      return (
        <li key={entry.key} className="rounded-lg bg-ground" data-testid="collection-item">
          {record
            ? <RecordCard record={record} onClick={() => setOpenRecord(record.id)} />
            : (
              <p className="p-5 pb-0 text-[15px] text-ink-55">
                This record isn’t there any more — it may have been replaced when a document was analysed again.
              </p>
            )}
          <div className={`px-5 sm:px-6 pb-5 space-y-4 ${record ? '' : 'pt-3'}`}>
            {renderNotes(entry)}
            {renderBar(entry, !!record)}
            {record && renderComments(entry)}
          </div>
        </li>
      );
    }
    return (
      <li key={entry.key} className="rounded-lg bg-ground p-5 sm:px-6 space-y-3" data-testid="collection-item">
        {highlight && (record
          ? (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><RecordLine rec={record} /></div>
              <Button size="sm" variant="secondary" className="shrink-0" onClick={() => setOpenRecord(record.id)} data-testid="collection-item-view">
                View record
              </Button>
            </div>
          )
          : <p className="text-[15px] text-ink-55">The record this was on isn’t there any more.</p>)}
        {highlight
          ? <blockquote className={`${colourClass(highlight.colour)} rounded-md px-3 py-2 text-[17px] text-ink leading-relaxed`}>“{highlight.quote}”</blockquote>
          : <p className="text-[15px] text-ink-55">This highlight was removed.</p>}
        {renderNotes(entry)}
        {renderBar(entry, !!highlight)}
        {highlight && renderComments(entry)}
      </li>
    );
  };

  const currentLabel = search.trim()
    ? `${shown.length} of ${entries.length} match “${search.trim()}”`
    : `${selected ? selected.name : 'Everything saved'} · ${shown.length}`;

  return (
    <AppShell>
      <main>
        <PageHeader
          title="Collections"
          lede={(
            <p>
              Your own sets of records and passages — things you want to come back to, share with someone, or ask about.
              Save a record from My Life or when you open one; save a passage by highlighting it.
            </p>
          )}
        />

        {collections === null ? (
          <p className="text-sm text-ink-55 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Opening your collections…</p>
        ) : locked ? (
          <p className="text-[15px] text-ink-70">Unlock your account to open your collections.</p>
        ) : error ? (
          <p className="text-[15px] text-ink-70" data-testid="collections-error">We couldn’t open your collections just now — try again in a little while.</p>
        ) : collections.length === 0 ? (
          <section className="py-6 max-w-[52ch]" data-testid="collections-empty">
            <div aria-hidden="true" className="w-[52px] h-[52px] bg-inset rounded-lg" />
            <h2 className="mt-5 font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink">No collections yet</h2>
            <p className="mt-2 text-base text-ink-70">
              Name one to start — “About my brother”, “Things to ask the council” — or open a record and choose
              “Save to a collection”.
            </p>
            <div className="mt-5">{newCollectionForm()}</div>
          </section>
        ) : (
          <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start">
            <nav className="rounded-lg bg-ground p-2 flex flex-col gap-0.5 lg:sticky lg:top-4" aria-label="Your collections">
              <button type="button" onClick={() => choose(null)} aria-current={!selected ? 'page' : undefined}
                className={`flex justify-between items-center px-3 py-2.5 rounded-md text-[15px] font-semibold ${!selected ? 'bg-inset text-ink' : 'text-ink-70 hover:bg-inset/60'}`}
                data-testid="collection-all">
                <span>Everything saved</span><span className="pd-mono">{totalSaved}</span>
              </button>
              {collections.map((c) => (
                <button key={c.id} type="button" onClick={() => choose(c.id)} aria-current={c.id === selectedId ? 'page' : undefined}
                  className={`flex justify-between items-center gap-3 px-3 py-2.5 rounded-md text-[15px] text-left ${c.id === selectedId ? 'bg-inset text-ink font-semibold' : 'text-ink-70 hover:bg-inset/60'}`}
                  data-testid={`collection-${c.id}`}>
                  <span className="min-w-0 break-words">{c.name}</span><span className="pd-mono shrink-0">{c.items.length}</span>
                </button>
              ))}
              <div className="mt-1 pt-2 border-t border-rule px-1 pb-1">
                {creating
                  ? newCollectionForm(true)
                  : (
                    <button type="button" onClick={() => setCreating(true)} className="w-full text-left px-2 py-2 rounded-md text-[15px] font-semibold text-brand hover:text-brand-ink" data-testid="collections-start-new">
                      New collection
                    </button>
                  )}
              </div>
            </nav>

            <section className="space-y-4 min-w-0" aria-live="polite" data-testid={selected ? 'collection-open' : 'collection-everything'}>
              {selected && (
                <div className="flex items-center gap-2 flex-wrap">
                  {renaming?.id === selected.id ? (
                    <>
                      <Input value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setRenaming(null); }}
                        aria-label="Collection name" className="max-w-xs" autoFocus />
                      <Button size="sm" onClick={rename}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <h2 className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink mr-2 break-words min-w-0">{selected.name}</h2>
                      <Button size="sm" variant="ghost" onClick={() => setRenaming({ id: selected.id, name: selected.name })}>Rename</Button>
                      <Button size="sm" variant="ghost" className="hover:text-breach-ink" onClick={() => setConfirmDelete(selected)} data-testid="collection-delete">Delete</Button>
                    </>
                  )}
                </div>
              )}

              {entries.length > 0 && (
                <>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search what you’ve saved" aria-label="Search what you’ve saved" className="pr-10" data-testid="collections-search" />
                      <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-ink-55" aria-hidden="true" />
                    </div>
                    <Select value={sort} onValueChange={setSort}>
                      <SelectTrigger className="sm:w-[170px]" aria-label="Sort"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(SORTS).map(([id, s]) => <SelectItem key={id} value={id}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="pd-eyebrow mr-1" data-testid="collections-current">{currentLabel}</span>
                    {KINDS.map((k) => (
                      <Pill key={k.id} on={kind === k.id} onClick={() => setKind(k.id)} testId={`collections-kind-${k.id}`}>{k.label}</Pill>
                    ))}
                  </div>
                </>
              )}

              {entries.length === 0 ? (
                <div className="rounded-lg bg-ground py-10 px-6 text-center">
                  <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">Nothing saved here yet</p>
                  <p className="text-base text-ink-55">Open a record and choose “Save to a collection”, or highlight a passage and save it.</p>
                </div>
              ) : shown.length === 0 ? (
                <div className="rounded-lg bg-ground py-10 px-6 text-center">
                  <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">Nothing matches that</p>
                  <p className="text-base text-ink-55">Try another word, or show everything.</p>
                </div>
              ) : (
                <ul className="flex flex-col gap-4">
                  {shown.map(renderEntry)}
                </ul>
              )}
            </section>
          </div>
        )}
      </main>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{confirmDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes the collection only. The records and your highlights stay where they are.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="collection-delete-confirm">Delete collection</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RecordDetail
        recordId={openRecord}
        open={!!openRecord}
        onOpenChange={(o) => { if (!o) { setOpenRecord(null); load(); loadComments(collections).catch(() => {}); } }}
        onOpenDocument={(docId, page) => navigate(`/documents?open=${docId}${page ? `&page=${page}` : ''}`)}
      />
    </AppShell>
  );
};

export default Collections;
